import { once } from 'lodash-es';
import React, { useCallback, useContext, useRef, useState } from 'react';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { ContentId } from 'src/utils/constants/contentId';
import { getErrorInfoFromStatusCode } from 'src/utils/errorUtils';
import { lookupEventInCatalog } from 'src/utils/eventWithDataUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  CatalogClient,
  EntityWithTickets,
  Event,
  Listing,
  Sale,
} from 'src/WebApiController';

import { useAppContext } from '../AppContext';
import { useCatalogDataContext } from '../CatalogDataContext';
import { Content } from '../ContentContext';
import { useSearchDataContext } from '../SearchDataContext';
import {
  getPosEntityStopwatchLoggingId,
  getStopwatchGapsForActivePosEntityPageLoad,
  StopwatchLoggingEventName,
  useStopwatchLoggingContext,
} from '../StopwatchLoggingContext';
import {
  GetListingEntityType,
  GetPurchaseEntityType,
  GetSaleEntityType,
  SetActivePosEntityType,
} from './MultiEntityContextProvider';

export type IPosEntity = {
  id: EntityWithTickets['id'];
  posEvId?: EntityWithTickets['posEvId'];
  viagVirtId: EntityWithTickets['viagVirtId'] | null;
  entityType: ActionOutboxEntityType;
};

export type IActivePosEntityContext<TDataItem extends IPosEntity> = {
  entityType: ActionOutboxEntityType;
  posEntityId?: number | null;
  /* Null means a value was not able to be obtained.  Undefined means the item was never retrieved or being retrieved. */
  posEntity?: TDataItem | null;
  /* Null means a value was not able to be obtained.  Undefined means the item was never retrieved or being retrieved. */
  event?: Event | null;
  /* Whether the current active event item is being searched or just loading */
  posEntityDisplayId?: string | null;
  setActivePosEntity: SetActivePosEntityType;
  updateActivePosEntityInfo: (
    posEntity: Pick<
      IActivePosEntityContext<TDataItem>,
      'posEntityId' | 'event' | 'posEntity' | 'posEntityDisplayId'
    >
  ) => void;
  isLoading: boolean;
  errorInfo?: { errorHeader: React.ReactNode; errorMessage: React.ReactNode };
};

export const createActivePosEntityContext = once(<
  TDataItem extends IPosEntity,
>() => React.createContext({} as IActivePosEntityContext<TDataItem>));
export const useActivePosEntityContext = <TDataItem extends IPosEntity>() =>
  useContext(createActivePosEntityContext<TDataItem>());

export const ActivePosEntityProvider = <TDataItem extends IPosEntity>({
  entityType,
  getActivePosEntity,
  getSaleEntity, // NEW - V1 ignores this
  getListingEntity, // NEW - V1 ignores this
  getPurchaseEntity, // NEW - V1 ignores this
  onSetActiveItemCompleteCallback,
  doNotLogPageLoad,
  children,
}: {
  entityType: ActionOutboxEntityType;
  getActivePosEntity: (
    posEntityId: number,
    curEntity?: TDataItem | null,
    dataToGet?: string[] | null
  ) => Promise<
    Pick<
      IActivePosEntityContext<TDataItem>,
      'posEntityId' | 'event' | 'posEntity' | 'posEntityDisplayId'
    >
  >;
  getSaleEntity?: GetSaleEntityType; // V1 ignores this
  getListingEntity?: GetListingEntityType; // V1 ignores this
  getPurchaseEntity?: GetPurchaseEntityType; // V1 ignores this

  /* Callback when item is successfully set active */
  onSetActiveItemCompleteCallback?: (
    item: TDataItem,
    forceRefresh?: boolean
  ) => void;
  doNotLogPageLoad?: boolean;
  children: React.ReactNode;
}) => {
  const { trackError } = useClientLogContext();

  const [errorInfo, setErrorInfo] = useState<{
    errorHeader: React.ReactNode;
    errorMessage: React.ReactNode;
  }>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const ActivePosEntityContext = createActivePosEntityContext<TDataItem>();
  const [posEntityInfo, setPosEntityInfo] = useState<
    Pick<
      IActivePosEntityContext<TDataItem>,
      'posEntityId' | 'posEntity' | 'event' | 'posEntityDisplayId'
    >
  >({});

  const { data, updateItemInEvent } = useCatalogDataContext();
  const searchDataContext = useSearchDataContext();
  const searchDataContextRef = useRef(searchDataContext);
  searchDataContextRef.current = searchDataContext;
  const { activeAccountWebClientConfig, mainRoute } = useAppContext();
  const { startEvent, addStopGap } = useStopwatchLoggingContext();

  const getAndUpdateCatalogDataForEntity = useCallback(
    async (item: TDataItem, sectional = false) => {
      if (
        !item?.viagVirtId ||
        (entityType !== ActionOutboxEntityType.Listing &&
          entityType !== ActionOutboxEntityType.Sale)
      ) {
        return null;
      }
      // Now that we got the listing details, try to get the Event from existing data
      let event = lookupEventInCatalog(data, item.viagVirtId, item.posEvId);

      // If we can't find it, go fetch it
      if (event == null) {
        const client = new CatalogClient(activeAccountWebClientConfig);

        const results =
          entityType === ActionOutboxEntityType.Listing
            ? await client.getCatalogForListingByListingId(item.id!)
            : await client.getCatalogForSaleBySaleId(item.id!);
        if (results?.events) {
          event = lookupEventInCatalog(results, item.viagVirtId, item.posEvId);
        }
      } else {
        // The event exists in the catalog, update the entity in that event
        if (entityType === ActionOutboxEntityType.Listing) {
          updateItemInEvent(item as unknown as Listing, entityType, sectional);
        } else {
          updateItemInEvent(item as unknown as Sale, entityType);
        }
      }

      // Also push to SearchDataContext localOverrides for V2 table updates
      if (searchDataContextRef.current.isEnabled) {
        // Derive internalNotesPreview from privNotes if not populated
        // (the detail API returns privNotes but not internalNotesPreview)
        const entity = item as unknown as Listing | Sale;
        if (entity.privNotes != null && entity.internalNotesPreview == null) {
          const maxLen = 80;
          entity.internalNotesPreview = entity.privNotes.length > maxLen
            ? entity.privNotes.substring(0, maxLen)
            : entity.privNotes;
          entity.isInternalNotesPreviewTruncated = entity.privNotes.length > maxLen;
        }

        if (entityType === ActionOutboxEntityType.Listing) {
          searchDataContextRef.current.updateItemInEvent(entity as Listing, entityType, sectional);
        } else {
          searchDataContextRef.current.updateItemInEvent(entity as Sale, entityType);
        }
      }

      return event;
    },
    [activeAccountWebClientConfig, data, entityType, updateItemInEvent]
  );

  const updateActivePosEntityInfo = useCallback(
    async (
      item: Pick<
        IActivePosEntityContext<TDataItem>,
        'posEntityId' | 'event' | 'posEntity' | 'posEntityDisplayId'
      >
    ) => {
      const event = item.posEntity
        ? await getAndUpdateCatalogDataForEntity(item.posEntity)
        : undefined;

      if (
        posEntityInfo.posEntityId == null ||
        posEntityInfo.posEntityId === item.posEntityId
      ) {
        // Only update the entity info if it's the active one
        setPosEntityInfo({ ...item, event: event?.event });
      }
    },
    [getAndUpdateCatalogDataForEntity, posEntityInfo.posEntityId]
  );

  const setActivePosEntity = useCallback(
    async (
      posEntityId: number,
      posEntityDisplayId?: string | null,
      forceRefresh?: boolean,
      dataToGet?: string[],
      resetData?: boolean,
      sectional = false,
      posEntityType?: ActionOutboxEntityType
    ) => {
      if (isLoading) {
        // NOTE: This is already a request pending, any new call - will either likely replace the old one or being replaced by the old one
        // depending on which ones finish first - so we just quit here as there's nothing really to do correctly
        // Importantly - the dev calling setActivePosEntity should know when to call this (and when not)
        return;
      }

      if (posEntityId === 0) {
        // Clearing the active item
        setPosEntityInfo({
          posEntityId: posEntityId,
          posEntityDisplayId: posEntityDisplayId,
        });
        return;
      }

      // Determine if this is a modal tab load
      const isDetailsModalTabLoad = Boolean(posEntityId);

      const shouldLogData = !doNotLogPageLoad && isDetailsModalTabLoad;

      const isSameEntity = posEntityInfo?.posEntityId === posEntityId;
      if (forceRefresh || !isSameEntity) {
        // If we are getting a different item, clear the existing ones so it shows a loading state
        setPosEntityInfo({
          posEntityId: posEntityId,
          posEntityDisplayId: posEntityDisplayId,
        });
      }

      const logKey = `ActivePosEntity-${entityType}-${posEntityId}${dataToGet?.length ? '-' + dataToGet.join('-') : ''}`;
      return tryInvokeApi(
        async () => {
          setErrorInfo(undefined);
          setIsLoading(true);

          if (shouldLogData) {
            startEvent(
              logKey,
              getPosEntityStopwatchLoggingId(entityType),
              getStopwatchGapsForActivePosEntityPageLoad(entityType),
              entityType
            );
          }

          const item = await getActivePosEntity(
            posEntityId,
            // if we want to reset data or the entity we want is not the same, pass in null for current entity
            resetData || !isSameEntity ? undefined : posEntityInfo?.posEntity,
            dataToGet
          );

          // null item return means it was not found (204 status)
          if (item == null) {
            setErrorInfo({
              errorHeader: <Content id={ContentId.Error_NotFound_Header} />,
              errorMessage: (
                <Content id={ContentId.Error_NotFound_GenericMessage} />
              ),
            });
          }

          // Once we found it, set the item as active
          const event = item.posEntity
            ? await getAndUpdateCatalogDataForEntity(item.posEntity, sectional)
            : undefined;

          setPosEntityInfo({ ...item, event: event?.event });

          // Add stop gap when data is loaded and set into context
          if (item.posEntity && shouldLogData) {
            addStopGap(
              logKey,
              logKey,
              getStopwatchGapsForActivePosEntityPageLoad(entityType),
              StopwatchLoggingEventName.EntityLoad,
              entityType
            );
          }

          if (item.posEntity) {
            onSetActiveItemCompleteCallback?.(item.posEntity, forceRefresh);
          }
        },
        (error) => {
          // Clear the active ids so on retry it will reget
          setPosEntityInfo({
            posEntityId: undefined,
            posEntityDisplayId: undefined,
          });
          const { headerDisplay, messageDisplay } = getErrorInfoFromStatusCode(
            error?.status,
            error?.message
          );
          setErrorInfo({
            errorHeader: headerDisplay,
            errorMessage: messageDisplay,
          });
          trackError('ActivePosEntityContext.getActivePosEntity', error, {
            posEntityId,
            posEntityDisplayId,
          });
        },
        () => {
          setIsLoading(false);
        }
      );
    },
    [
      isLoading,
      doNotLogPageLoad,
      posEntityInfo?.posEntityId,
      posEntityInfo?.posEntity,
      entityType,
      getActivePosEntity,
      getAndUpdateCatalogDataForEntity,
      startEvent,
      addStopGap,
      onSetActiveItemCompleteCallback,
      trackError,
    ]
  );

  return (
    <ActivePosEntityContext.Provider
      value={{
        entityType,
        isLoading,
        errorInfo,
        setActivePosEntity,
        updateActivePosEntityInfo: updateActivePosEntityInfo,
        ...posEntityInfo,
        posEntityId: posEntityInfo.posEntity?.id || posEntityInfo.posEntityId,
      }}
    >
      {children}
    </ActivePosEntityContext.Provider>
  );
};
